import { v4 as uuidv4 } from "uuid";
import { apiRunBacktest, apiBacktestHealth } from "api/apiPredictions";

// Everything the backend engine (dashboard_backend/api/predictions/backtest.js)
// reads from backtestSettings, minus predictionsTable (the server fetches
// predictions itself from symbol/models/from/to). Explicit allowlist so
// nothing unexpected is sent.
const SETTINGS_KEYS = [
  "takeProfitPercent",
  "stopLossPercent",
  "enterPositionBuyCount",
  "minPredictionProba",
  "exitPositionNoBuyCount",
  "daysOpenExitCount",
  "initialBalance",
  "orderSize",
  "orderSizeType",
  "maxOpenOrders",
  "resetTrendAfterSell",
  "probaSizingEnabled",
  "probaSizingBoostThreshold",
  "userCommission",
  "premiumPerShare",
  "slippage",
  "startDate",
  "endDate",
];

const pickSettings = (backtestSettings) =>
  SETTINGS_KEYS.reduce((acc, key) => {
    acc[key] = backtestSettings[key];
    return acc;
  }, {});

const buildRequest = (backtestSettings, opts) => ({
  uuid: opts.uuid || uuidv4(),
  symbol: backtestSettings.symbol,
  models: opts.models,
  from: opts.from,
  to: opts.to,
  settings: pickSettings(backtestSettings),
});

/**
 * Runs a backtest on the server engine (dashboard_backend backtest.js), the
 * only engine -- the old client-side copy was removed so stats live in one
 * place.
 *
 * @param {object} backtestSettings - settings incl. symbol and predictionsTable
 * @param {object} [opts]
 * @param {object} [opts.intraday] - { enabled, regularSessionOnly, takeProfit, stopLoss, trailingTakeProfit }
 * @param {string} [opts.from] - predictions fetch range start, e.g. results.startDate
 * @param {string} [opts.to] - predictions fetch range end, e.g. results.endDate
 * @param {string[]} [opts.models] - model ids, e.g. results.selectedModels
 * @param {string} [opts.uuid] - socket progress channel; subscribe to it with
 *   socket.on(uuid, ...) BEFORE calling this, since progress events can arrive
 *   while the request is still in flight. A fresh one is minted if omitted.
 * @param {AbortSignal} [opts.signal] - aborts a superseded request
 */
export const runBacktest = async (backtestSettings, opts = {}) => {
  const { strategy } = await apiRunBacktest(
    { ...buildRequest(backtestSettings, opts), intraday: opts.intraday },
    opts.signal
  );

  // predictions is intentionally omitted from the server response (the
  // client already holds this array) -- re-attach it and mint an _id
  return {
    ...strategy,
    _id: uuidv4(),
    predictions: backtestSettings.predictionsTable,
  };
};

/**
 * Health check for the same backtest: holdout, year-by-year, parameter
 * plateau and edge, graded into a verdict (backend backtest-health.js).
 * Same opts as runBacktest, minus intraday (daily exits only).
 */
export const runBacktestHealth = (backtestSettings, opts = {}) =>
  apiBacktestHealth(buildRequest(backtestSettings, opts), opts.signal);
